import { onMounted, onUnmounted, nextTick } from "vue";

export interface UseDragRotationOptions {
  /**
   * CSS selector for the container element to observe
   * @default '.flex.flex-col'
   */
  containerSelector?: string;
  
  /**
   * CSS selector for the dragged element
   * @default '.dragging'
   */
  dragClassSelector?: string;
  
  /**
   * Base rotation angle in degrees
   * @default -2.25
   */
  baseRotation?: number;
  
  /**
   * Velocity multiplier for X axis
   * @default 3
   */
  velocityMultiplierX?: number;
  
  /**
   * Velocity multiplier for Y axis
   * @default 3.5
   */
  velocityMultiplierY?: number;
  
  /**
   * Minimum rotation adjustment in degrees
   * @default -5.5
   */
  minAdjustment?: number;
  
  /**
   * Maximum rotation adjustment in degrees
   * @default 5.5
   */
  maxAdjustment?: number;
  
  /**
   * Damping factor for velocity smoothing (0-1, higher = more damping)
   * @default 0.85
   */
  dampingFactor?: number;
}

/**
 * Composable for adding dynamic rotation effects to draggable elements.
 * Works with vue3-smooth-dnd and other drag libraries that add a CSS class when dragging.
 * 
 * @example
 * ```vue
 * <script setup>
 * import { useDragRotation } from '@/app/composables/useDragRotation';
 * 
 * const { onDragStart, onDragEnd } = useDragRotation({
 *   containerSelector: '.my-drag-container'
 * });
 * </script>
 * 
 * <template>
 *   <Container @drag-start="onDragStart" @drag-end="onDragEnd">
 *     <!-- draggable items -->
 *   </Container>
 * </template>
 * ```
 */
export function useDragRotation(options: UseDragRotationOptions = {}) {
  const {
    containerSelector = '.flex.flex-col',
    dragClassSelector = '.dragging',
    velocityMultiplierX = 3,
    velocityMultiplierY = 3.5,
    minAdjustment = -5.5,
    maxAdjustment = 5.5,
    dampingFactor = 0.85,
  } = options;

  // Mouse movement tracking state
  let lastMouseX = 0;
  let lastMouseY = 0;
  let lastTime = 0;
  let velocityX = 0;
  let velocityY = 0;
  let isDragging = false;
  let draggedElement: HTMLElement | null = null;
  let dragObserver: MutationObserver | null = null;

  function updateRotation(event: MouseEvent) {
    if (!isDragging || !draggedElement) return;

    const currentTime = Date.now();
    const deltaTime = currentTime - lastTime;
    
    if (deltaTime > 0 && lastTime > 0) {
      const deltaX = event.clientX - lastMouseX;
      const deltaY = event.clientY - lastMouseY;
      
      // Calculate velocity (pixels per millisecond) with smoothing
      const newVelocityX = deltaX / deltaTime;
      const newVelocityY = deltaY / deltaTime;
      
      // Smooth velocity changes (damping factor)
      velocityX = velocityX * dampingFactor + newVelocityX * (1 - dampingFactor);
      velocityY = velocityY * dampingFactor + newVelocityY * (1 - dampingFactor);
      
      // Calculate rotation adjustment based on velocity
      const adjustment = Math.max(
        minAdjustment,
        Math.min(maxAdjustment, velocityX * velocityMultiplierX + velocityY * velocityMultiplierY)
      );
      // Rotation is the adjustment itself, baseRotation is applied via CSS default
      const rotationAngle = adjustment;
      
      // Set CSS variable for rotation
      draggedElement.style.setProperty('--drag-rotation', `${rotationAngle}deg`);
    }
    
    lastMouseX = event.clientX;
    lastMouseY = event.clientY;
    lastTime = currentTime;
  }

  function onDragStart() {
    // Prevent double-firing
    if (isDragging) return;
    
    isDragging = true;
    lastTime = 0; // Reset to allow initialization
    
    // Use nextTick to ensure DOM has updated with drag class
    nextTick(() => {
      // Find dragged element - it might be on the wrapper or the card itself
      const dragClass = dragClassSelector.replace('.', '');
      draggedElement = document.querySelector(dragClassSelector) as HTMLElement;
      if (!draggedElement) {
        // Try finding it by traversing up from any draggable item
        const allDraggables = document.querySelectorAll('[class*="smooth-dnd"]');
        for (const el of allDraggables) {
          if (el.classList.contains(dragClass)) {
            draggedElement = el as HTMLElement;
            break;
          }
        }
      }
    });
    
    // Attach mouse tracking immediately
    const mouseMoveHandler = (e: MouseEvent) => {
      // Find dragged element on first move if not found yet
      if (!draggedElement) {
        draggedElement = document.querySelector(dragClassSelector) as HTMLElement;
        if (!draggedElement) return;
      }
      
      if (lastTime === 0) {
        // First move - initialize position
        lastMouseX = e.clientX;
        lastMouseY = e.clientY;
        lastTime = Date.now();
        return;
      }
      
      updateRotation(e);
    };
    
    document.addEventListener('mousemove', mouseMoveHandler);
    
    // Store handler globally for cleanup (using a unique key)
    const handlerKey = `_dragRotationMouseHandler_${Date.now()}`;
    (window as any)[handlerKey] = mouseMoveHandler;
    if (draggedElement) {
      draggedElement.setAttribute('_dragRotationHandlerKey', handlerKey);
    }
  }

  function onDragEnd() {
    // Prevent double-firing
    if (!isDragging) return;
    
    isDragging = false;
    
    // Remove mouse move handler
    const handlerKey = draggedElement?.getAttribute('_dragRotationHandlerKey');
    if (handlerKey) {
      const handler = (window as any)[handlerKey];
      if (handler) {
        document.removeEventListener('mousemove', handler);
        delete (window as any)[handlerKey];
      }
      draggedElement?.removeAttribute('_dragRotationHandlerKey');
    }
    
    if (draggedElement) {
      // Reset rotation (CSS will apply the default baseRotation)
      draggedElement.style.removeProperty('--drag-rotation');
      draggedElement = null;
    }
    
    // Reset velocity and tracking
    velocityX = 0;
    velocityY = 0;
    lastMouseX = 0;
    lastMouseY = 0;
    lastTime = 0;
  }

  // Set up MutationObserver as a fallback to detect dragging state
  onMounted(() => {
    // Watch for drag class changes as a fallback
    dragObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target as HTMLElement;
          const dragClass = dragClassSelector.replace('.', '');
          const hasDragging = target.classList.contains(dragClass);
          
          if (hasDragging && !isDragging) {
            // Drag started
            onDragStart();
          } else if (!hasDragging && isDragging && draggedElement === target) {
            // Drag ended for this specific element
            onDragEnd();
          }
        }
      }
    });

    // Observe the container for class changes on draggable elements
    nextTick(() => {
      const container = document.querySelector(containerSelector);
      if (container && dragObserver) {
        dragObserver.observe(container, {
          attributes: true,
          attributeFilter: ['class'],
          subtree: true
        });
      }
    });
  });

  onUnmounted(() => {
    if (dragObserver) {
      dragObserver.disconnect();
    }
    onDragEnd(); // Cleanup on unmount
  });

  return {
    onDragStart,
    onDragEnd,
  };
}

